import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CsuiteRole } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export const salesCsuiteMembersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sales/csuite-members',
  component: SalesCsuiteMembersPage,
});

const CSUITE_ROLES: readonly CsuiteRole[] = ['ceo', 'coo', 'cto', 'cfo', 'cmo', 'other'];

interface Row {
  personId: string;
  name: string;
  email: string;
  isCsuite: boolean;
  csuiteRole: CsuiteRole | null;
}

function SalesCsuiteMembersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState<Record<string, CsuiteRole>>({});

  const peopleQuery = useQuery({
    queryKey: ['people-simple'],
    queryFn: () => api.listPeopleSimple(),
  });
  const membersQuery = useQuery({
    queryKey: ['sales-csuite-members'],
    queryFn: () => api.listCsuiteMembers(),
  });

  const upsertMutation = useMutation({
    mutationFn: (payload: { personId: string; isCsuite: boolean; csuiteRole: CsuiteRole | null }) =>
      api.upsertCsuiteMember(payload),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['sales-csuite-members'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const rows: Row[] = React.useMemo(() => {
    const people = peopleQuery.data ?? [];
    const members = membersQuery.data ?? [];
    const memberIndex = new Map(members.map((m) => [m.personId, m]));
    const merged: Row[] = people.map((p) => {
      const m = memberIndex.get(p.id);
      return {
        personId: p.id,
        name: p.name,
        email: p.email,
        isCsuite: m ? m.isCsuite : false,
        csuiteRole: m ? m.csuiteRole : null,
      };
    });
    merged.sort((a, b) => {
      if (a.isCsuite !== b.isCsuite) return a.isCsuite ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    return merged;
  }, [peopleQuery.data, membersQuery.data]);

  const setRole = (personId: string, role: CsuiteRole): void => {
    setPending((prev) => ({ ...prev, [personId]: role }));
  };

  return (
    <AppShell>
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
          C-Suite Members
        </h1>
        <p className="text-base text-[hsl(var(--muted-foreground))]">
          Promote people to C-suite for weighting in the sales dashboard. Admin only.
        </p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>People</CardTitle>
          <CardDescription>
            {rows.length} total · {rows.filter((r) => r.isCsuite).length} C-suite
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No people found.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {rows.map((m) => {
                const nextRole = pending[m.personId] ?? m.csuiteRole ?? 'other';
                return (
                  <li
                    key={m.personId}
                    className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm"
                  >
                    <div>
                      <div className="font-medium">{m.name}</div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))]">{m.email}</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {m.isCsuite ? (
                        <Badge variant="default">
                          {m.csuiteRole ? m.csuiteRole.toUpperCase() : 'C-suite'}
                        </Badge>
                      ) : (
                        <Badge variant="muted">not C-suite</Badge>
                      )}
                      <Select
                        value={nextRole}
                        onChange={(e) => setRole(m.personId, e.currentTarget.value as CsuiteRole)}
                        className="h-9 w-32"
                      >
                        {CSUITE_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r.toUpperCase()}
                          </option>
                        ))}
                      </Select>
                      {m.isCsuite ? (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              upsertMutation.mutate({
                                personId: m.personId,
                                isCsuite: true,
                                csuiteRole: nextRole,
                              })
                            }
                            disabled={upsertMutation.isPending}
                          >
                            Update role
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() =>
                              upsertMutation.mutate({
                                personId: m.personId,
                                isCsuite: false,
                                csuiteRole: null,
                              })
                            }
                            disabled={upsertMutation.isPending}
                          >
                            Demote
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() =>
                            upsertMutation.mutate({
                              personId: m.personId,
                              isCsuite: true,
                              csuiteRole: nextRole,
                            })
                          }
                          disabled={upsertMutation.isPending}
                        >
                          Promote
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
